// resolveEdit — the SR-Edit pipeline call behind `commitEdit`.
//
// PRD: Ralph/VisualQueryEditorEdit/PRD.md § Slice: Commit Pipeline.
//
// Wraps the (extractQueryStructure → applyStructuralEdit → emitSource) flow
// against a `SqlResolverService`. Pure: never mutates Monaco, never mutates
// the cached `ParsedBuffer`. Every edit funnels here so nothing
// parallel-implements byte-range patching (Codebase Pattern in
// `Ralph/VisualQueryEditorEdit/PROGRESS.md`).
//
// Error handling: SR-Edit's `EmitSourceError` (codes `overlapping-edits`,
// `edit-out-of-bounds`, `inverted-range`, `edit-inside-comment`) is the only
// error class we expect — we let it propagate so the caller surfaces a loud
// failure instead of silently corrupting SQL.

import type { StatementInfo } from '../../../sql-grammar/model';
import type { ModifiedStatement, StructuralEdit } from '../../../sql-resolver/types';
import type { SqlResolverService } from '../../../sql-resolver/service/resolverService';

export type ResolveEditCtx = {
  readonly service: SqlResolverService;
  readonly documentId: string;
  readonly statementId: string;
  // Optional per-branch target for VQE set-op branch editing (VQE-3 Slice 2).
  // When SET, this is the apply target and `statementId` is NOT consulted for
  // the structural-edit call (they are mutually exclusive on the apply side;
  // `statementId` remains the parent id used only as a pending-edit label).
  // When ABSENT, behavior is byte-identical to the top-level `statementId` path.
  readonly statementInfo?: StatementInfo;
};

export type ResolvedEdit = {
  readonly modified: ModifiedStatement;
  readonly emittedSource: string;
  readonly originalSource: string;
};

export function resolveEdit(edit: StructuralEdit, ctx: ResolveEditCtx): ResolvedEdit {
  const { service, documentId, statementId } = ctx;
  const buffer = service.getParsedBuffer(documentId);
  if (!buffer) {
    throw new Error(
      `resolveEdit: no document '${documentId}' is open in the resolver service`,
    );
  }
  const modified = ctx.statementInfo
    ? service.applyStructuralEditForStatement(documentId, ctx.statementInfo, edit)
    : service.applyStructuralEdit(documentId, statementId, edit);
  const emittedSource = service.emitSource(documentId, modified);
  return {
    modified,
    emittedSource,
    originalSource: buffer.parsedDocument.source,
  };
}
